import { Notification, PaginatedResponse } from "@ac-expo/api-types";
import { BellSlash } from "@phosphor-icons/react/dist/ssr";
import { FileType } from "@prisma/client";
import { InfiniteData, UseMutationResult } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { cn } from "../../../utils";
import { Button } from "../Button";
import { RedDot } from "../Dot";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Separator } from "../Separator";
import { SvgButton } from "../SvgButton";

type NotificationContextType = {
  notifications: InfiniteData<PaginatedResponse<Notification>> | undefined;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isLoading: boolean;
};

type NotificationPopoverProps = {
  navigateTo: (url: string) => void;
  useNotificationContext: () => NotificationContextType;
  getFileUrl: (key?: string, fileType?: FileType, width?: number) => string;
  useReadManyNotificationMutation: () => UseMutationResult<
    void,
    unknown,
    string[],
    unknown
  >;
  useReadNotificationMutation: () => UseMutationResult<
    void,
    unknown,
    string,
    unknown
  >;
  bellIcon?: React.ReactNode;
};

export const NotificationPopover = ({
  navigateTo,
  useNotificationContext,
  getFileUrl,
  useReadManyNotificationMutation,
  useReadNotificationMutation,
  bellIcon,
}: NotificationPopoverProps) => {
  const { mutate: readManyNotifications } = useReadManyNotificationMutation();
  const { mutate: markAsRead } = useReadNotificationMutation();
  const { notifications, fetchNextPage, isLoading } = useNotificationContext();

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && !isLoading) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, isLoading]);

  const onNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (!notification.url) {
      return;
    }
    navigateTo(notification.url);
  };

  const hasNotification =
    notifications?.pages?.[0]?.count !== undefined &&
    notifications.pages[0].count > 0;
  const hasNotReadNotifications =
    hasNotification && !notifications?.pages?.[0]?.data?.[0]?.read;

  const readAll = () => {
    const notificationIds = notifications?.pages.flatMap((n) =>
      n.data.flatMap((n) => n.id)
    );

    if (!notificationIds) {
      return;
    }

    readManyNotifications(notificationIds);
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      return;
    }
    readAll();
  };

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <div className="relative inline-block cursor-pointer rounded-full">
          {bellIcon ? (
            bellIcon
          ) : (
            <SvgButton
              name="bell"
              alt="cloche"
              className="text-black"
              svgClassName="text-black"
            />
          )}

          {hasNotReadNotifications && (
            <RedDot
              className={cn("absolute", {
                "-right-1.5 bottom-0 size-3": !bellIcon,
                "bottom-0 right-0 size-2": bellIcon,
              })}
            />
          )}
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="size-fit max-h-[500px] w-fit min-w-[240px] overflow-y-auto p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {!hasNotification && (
          <div className="flex h-32 flex-col items-center justify-center gap-2 px-6">
            <BellSlash width={24} height={24} fill={"#98A2B3"} />
            <div className="text-center text-[16px] font-semibold leading-[24px] text-[#667085]">
              Aucune notification
            </div>
          </div>
        )}
        {notifications && hasNotification && (
          <>
            {notifications.pages.map((n) => (
              <div key={n.pageIndex}>
                {n.data.map((n) => (
                  <li className="contents" key={n.id}>
                    <Button
                      variant="null"
                      className={cn(
                        "flex h-fit w-full items-start gap-4 rounded-none px-3.5 py-3 focus:border-transparent focus:outline-none focus:ring-0",
                        {
                          "bg-[#ECEEF0]": n.read,
                          "bg-white": !n.read,
                        }
                      )}
                      onClick={() => onNotificationClick(n)}
                    >
                      {n.picture && (
                        <div className="relative inline-block size-[32px] cursor-pointer justify-center gap-4 rounded-full">
                          <img
                            src={
                              n.picture.key
                                ? getFileUrl(n.picture.key, "media")
                                : "/images/anonymous_avatar.svg"
                            }
                            alt="avatar"
                            className="size-[32px] rounded-full object-cover"
                          />
                          {!n.read && (
                            <span className="absolute bottom-0 right-0 size-3 rounded-full border border-white bg-red-500" />
                          )}
                        </div>
                      )}
                      <div className="flex flex-1 flex-col items-start">
                        <p className="whitespace-pre-wrap text-start text-[14px] leading-[21px] select-text">
                          {n.description}
                        </p>
                        <p className="text-[12px] leading-[18px] text-[#667085]">
                          il y a{" "}
                          {formatDistanceToNow(new Date(n.createdAt), {
                            locale: fr,
                          })}
                        </p>
                      </div>
                    </Button>
                    <Separator />
                  </li>
                ))}
              </div>
            ))}
            {!isLoading && <div ref={ref} />}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
